// AdminOfferClaims — the approval queue for manual-review offer claims.
//
// Card grid (no tables) so phones don't get horizontal scroll. State
// pill tabs (Pending / Credited / Rejected). Bulk-select on Pending tab.
// Light + dark designed.
//
// Phase 4 UI overhaul (2026-05-15): mounted inside RewardsHub as the
// "Claims" sub-tab — the hub already provides the outer max-w, the
// "Rewards" page title, and the sub-tab strip. This panel now drops:
//   • its outer `max-w-screen-2xl` wrapper
//   • the stale "Back to More" link
//   • the duplicated "Reward claims" page header
// Surface tokens align with the Phase 2/3 design system: ring-1 ring-inset
// arena borders, arena/neon text, violet admin accent. Data + API
// calls + multipart-shaped flows are unchanged.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type ClaimRow = {
  id: number;
  offer_id: number;
  user_id: number;
  user_first_name?: string | null;
  user_last_name?: string | null;
  trigger_kind: string | null;
  trigger_ref: string | null;
  computed_amount: number;
  state: 'pending'|'approved'|'rejected'|'credited'|'expired';
  decided_by_admin_id: number | null;
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
  created_at: string;
  offer_title: string;
  offer_type: string;
  /** Phase 14: artwork URL for the offer the player claimed (null if
   *  none uploaded). Lets the reviewer see exactly which artwork the
   *  player saw when they tapped Claim. */
  offer_image_url?: string | null;
  user_username: string;
};

type Tab = 'pending' | 'credited' | 'rejected';
const LIMIT = 50;

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'pending',  label: 'Pending' },
  { key: 'credited', label: 'Credited' },
  { key: 'rejected', label: 'Rejected' },
];

export default function ClaimsQueuePanel() {
  const [tab, setTab] = useState<Tab>('pending');
  const [rows, setRows] = useState<ClaimRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<{ rows: ClaimRow[]; total: number }>(
        `/admin/offer-claims?state=${tab}&limit=${LIMIT}&offset=${offset}`
      );
      setRows(r.rows);
      setTotal(r.total);
      setSelected(new Set());
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, offset]);

  async function approveOne(id: number) {
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/offer-claims/${id}/approve`);
      setToast('Claim approved + credited');
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  async function rejectOne(id: number) {
    const note = window.prompt('Reason for rejection (optional):') ?? null;
    if (note === null) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/offer-claims/${id}/reject`, note ? { note } : {});
      setToast('Claim rejected');
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  async function bulkApprove() {
    if (selected.size === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await adminApi.post<{ processed: number; failed: Array<{id: number; error: string}> }>(
        `/admin/offer-claims/bulk-approve`, { ids: Array.from(selected) }
      );
      setToast(`${r.processed} approved · ${r.failed.length} failed`);
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  // Phase 19 / Batch 6 #22: bulk-reject — opens DangerConfirmModal with
  // optional reason, then fires POST /admin/offer-claims/bulk-reject.
  // The modal handles cancel + busy state; we just await the API.
  const [confirmBulkReject, setConfirmBulkReject] = useState(false);
  async function bulkReject(reason?: string) {
    if (selected.size === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await adminApi.post<{ processed: number; rejected: number[]; failed: Array<{id: number; error: string}> }>(
        `/admin/offer-claims/bulk-reject`,
        reason ? { ids: Array.from(selected), note: reason } : { ids: Array.from(selected) }
      );
      setToast(`${r.processed} rejected · ${r.failed.length} failed`);
      setConfirmBulkReject(false);
      await load();
    } catch (e) {
      setErr((e as ApiError).message);
      throw e;  // re-raise so DangerConfirmModal stays open with inline error
    } finally { setBusy(false); }
  }

  function toggle(id: number) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  }
  function toggleAll() {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }

  return (
    // Phase 4 (2026-05-15): no outer max-w wrapper — RewardsHub owns it.
    <div className="space-y-4">
      {/* Compact summary row replaces the duplicated page header.
          Renders just the count + state tabs (kept here so the count
          stays adjacent to the active tab pill). */}
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12.5px] text-arena-600 dark:text-neon-200/70">
          <b className="text-arena-900 dark:text-white tabular-nums">{total}</b> {tab} claim{total === 1 ? '' : 's'}
        </div>
        {/* State tabs — quiet segmented look matching the Phase 1 TabHub
            'segmented' variant. Violet active fill matches admin brand. */}
        <div className="inline-flex rounded-full p-1
                        bg-arena-100/60 dark:bg-arena-800/40
                        ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setOffset(0); }}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold capitalize transition
                  ${active
                    ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                    : 'text-arena-700 dark:text-neon-200/70 hover:text-arena-900 dark:hover:text-white'}`}
              >{t.label}</button>
            );
          })}
        </div>
      </motion.div>

      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {/* Bulk action bar — only on Pending tab */}
      {tab === 'pending' && rows.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button onClick={toggleAll}
                  className="text-[12px] font-semibold text-violet-700 dark:text-violet-300 hover:underline">
            {selected.size === rows.length ? 'Clear selection' : `Select all ${rows.length}`}
          </button>
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              {/* Batch 6 #22: bulk-reject sits next to bulk-approve.
                  Rose with red glow, opens DangerConfirmModal — never
                  fires immediately on click (rejection is destructive
                  + audit-logged). */}
              <button onClick={() => setConfirmBulkReject(true)} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                 bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                                 text-white text-[12.5px] font-bold
                                 shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                                 transition active:scale-95 disabled:opacity-50">
                <Icon name="close" size={14} />
                Reject {selected.size} selected
              </button>
              <button onClick={bulkApprove} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                                 text-white text-[12.5px] font-bold
                                 shadow-[0_2px_10px_rgba(16,185,129,0.4)]
                                 transition active:scale-95 disabled:opacity-50">
                {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
                Approve {selected.size} selected
              </button>
            </div>
          )}
        </div>
      )}

      {/* Batch 6 #22: bulk-reject confirmation. Reason is logged to the
          claim's decision_note (visible in the Rejected tab + audit log). */}
      {confirmBulkReject && (
        <DangerConfirmModal
          open={true}
          onClose={() => setConfirmBulkReject(false)}
          onConfirm={(reason) => bulkReject(reason)}
          title={`Reject ${selected.size} claim${selected.size === 1 ? '' : 's'}?`}
          description={
            <>
              Marks {selected.size} pending claim{selected.size === 1 ? '' : 's'} as <strong>rejected</strong>.
              Players don't get credited. The reason below is stored on each claim's decision_note and
              shows up in the Rejected tab + audit log. This can be undone only by manually re-creating
              the claim.
            </>
          }
          confirmLabel="Reject"
          cancelLabel="Cancel"
          reasonLabel="Reason (optional · logged on each claim)"
          reasonPlaceholder="e.g. duplicate claims; offer terms not met"
        />
      )}

      {/* Cards */}
      <div>
        {loading && rows.length === 0 && (
          <div className="rounded-2xl bg-white dark:bg-arena-700/40
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                          backdrop-blur p-8 text-center text-[13px] text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" />
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl bg-white dark:bg-arena-700/40
                          ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                          shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                          backdrop-blur p-10 text-center">
            <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                            bg-arena-100/60 dark:bg-arena-800/60 mb-3">
              <Icon name="redeem" size={26} className="text-arena-400 dark:text-neon-200/40" />
            </div>
            <p className="text-[13px] text-arena-800 dark:text-white font-bold">No {tab} claims</p>
          </div>
        )}
        {rows.length > 0 && (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {rows.map((c) => (
              <li key={c.id}>
                <ClaimCard
                  c={c} tab={tab}
                  selected={selected.has(c.id)} busy={busy}
                  onToggle={() => toggle(c.id)}
                  onApprove={() => approveOne(c.id)}
                  onReject={() => rejectOne(c.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-end gap-2 pt-1 text-[12px] text-arena-600 dark:text-neon-200/60">
          <button disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/80
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums px-1">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/80
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Claim card ────────────────────────────────────────────────────

function ClaimCard({ c, tab, selected, busy, onToggle, onApprove, onReject }: {
  c: ClaimRow; tab: Tab; selected: boolean; busy: boolean;
  onToggle: () => void; onApprove: () => void; onReject: () => void;
}) {
  const STATE_PILL: Record<string, string> = {
    pending:  'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-200 dark:ring-amber-400/30',
    credited: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-200 dark:ring-emerald-400/30',
    rejected: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-200 dark:ring-rose-400/30',
    approved: 'bg-cyan-50 text-cyan-800 ring-1 ring-inset ring-cyan-200/60 dark:bg-cyan-400/10 dark:text-cyan-200 dark:ring-cyan-400/30',
    expired:  'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-700/50 dark:text-neon-200/70 dark:ring-arena-600/40',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-4
                  bg-white dark:bg-arena-700/40
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                  backdrop-blur
                  ${selected
                    ? 'ring-2 ring-violet-400 dark:ring-violet-400/60'
                    : 'ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 hover:ring-violet-300/60 dark:hover:ring-violet-400/30 transition'}`}>
      <div className="flex items-start gap-3">
        {tab === 'pending' && (
          <input
            type="checkbox" checked={selected} onChange={onToggle}
            className="mt-1 w-4 h-4 accent-violet-500 shrink-0"
          />
        )}
        {/* Phase 14: artwork thumbnail beside the player + offer line.
            Helps the reviewer instantly tie a claim back to the visual
            the player saw when they tapped Claim. */}
        {c.offer_image_url && (
          <div className="relative w-12 h-12 rounded-xl overflow-hidden
                          bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]
                          ring-1 ring-inset ring-white/10 grid place-items-center shrink-0">
            {/* Phase 16.5: cyan/brand halos removed; small black
                drop-shadow scaled to the 48px thumb. */}
            <img
              src={c.offer_image_url.replace(/\.png$/, '.thumb.png')}
              alt=""
              loading="lazy"
              decoding="async"
              className="relative w-[78%] h-[78%] object-contain
                         drop-shadow-[0_2px_3px_rgba(0,0,0,0.20)]"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {/* Batch 6: routed at canonical /people/players/:id (was legacy /users/:id). */}
          <Link to={`/people/players/${c.user_id}`}
                className="text-[13.5px] font-semibold text-arena-900 dark:text-white hover:underline truncate block">
            {(((c.user_first_name || "") + " " + (c.user_last_name || "")).trim() || "—")}
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/60 ml-1 font-normal">· #{c.user_id}</span>
          </Link>
          {/* 2026-05-16 (Routing Restructure): canonical offer URL is /offers/:id. */}
          <Link to={`/offers/${c.offer_id}`}
                className="text-[12px] text-violet-700 dark:text-violet-300 hover:underline truncate block">
            {c.offer_title}
          </Link>
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/40 mt-0.5">
            {fmtDate(c.created_at)} · type {c.offer_type}
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5
                         bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60
                         dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30
                         text-[14px] font-bold tabular-nums">
          {inr(c.computed_amount)}
        </span>
      </div>

      {(c.trigger_kind || c.trigger_ref) && (
        <div className="mt-3 rounded-xl bg-arena-50/60 dark:bg-arena-800/40
                        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                        px-3 py-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/60">Trigger · </span>
          <span className="font-mono text-arena-900 dark:text-white">{c.trigger_kind || '—'}</span>
          {c.trigger_ref && <span className="ml-2 text-arena-600 dark:text-neon-200/60">{c.trigger_ref}</span>}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${STATE_PILL[c.state]}`}>
            {c.state}
          </span>
          {c.ledger_id && c.state === 'credited' && (
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/60">Ledger #{c.ledger_id}</span>
          )}
        </div>
        {tab === 'pending' && (
          <div className="flex items-center gap-2">
            <button onClick={onReject} disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold transition disabled:opacity-50
                               bg-white text-rose-700 ring-1 ring-inset ring-rose-200/60 hover:bg-rose-50
                               dark:bg-arena-700/40 dark:text-rose-300 dark:ring-rose-400/30 dark:hover:bg-rose-400/10">
              <Icon name="close" size={13} /> Reject
            </button>
            <button onClick={onApprove} disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold transition disabled:opacity-50
                               bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700
                               shadow-[0_2px_10px_rgba(16,185,129,0.4)]">
              <Icon name="check" size={13} /> Approve
            </button>
          </div>
        )}
        {tab === 'rejected' && c.decision_note && (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/60 italic truncate max-w-[60%]">"{c.decision_note}"</div>
        )}
      </div>
    </motion.div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success'; children: React.ReactNode }) {
  const M = {
    error:   'bg-rose-50 ring-rose-200/60 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 ring-emerald-200/60 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200',
  } as const;
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>;
}
